// src/components/AIDecisionsTab.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient.js';
import { userKey } from '../utils/storageHelper.js';

const ALL_DECISION_TYPES = [
  // Finance
  { value: 'loan_approval', label: 'Loan Approval', industry: 'Finance' },
  { value: 'credit_scoring', label: 'Credit Scoring', industry: 'Finance' },
  { value: 'fraud_flag', label: 'Fraud Flag', industry: 'Finance' },
  { value: 'investment_recommendation', label: 'Investment Recommendation', industry: 'Finance' },
  { value: 'risk_assessment', label: 'Risk Assessment', industry: 'Finance' },
  // Healthcare
  { value: 'diagnosis', label: 'Diagnosis', industry: 'Healthcare' },
  { value: 'treatment_recommendation', label: 'Treatment Recommendation', industry: 'Healthcare' },
  { value: 'benefits_eligibility', label: 'Benefits Eligibility', industry: 'Healthcare' },
  { value: 'patient_triage', label: 'Patient Triage', industry: 'Healthcare' },
  // Insurance
  { value: 'claim_approval', label: 'Claim Approval', industry: 'Insurance' },
  { value: 'underwriting', label: 'Underwriting', industry: 'Insurance' },
  { value: 'premium_calculation', label: 'Premium Calculation', industry: 'Insurance' },
  { value: 'risk_screening', label: 'Risk Screening', industry: 'Insurance' },
  // HR & Legal
  { value: 'cv_screening', label: 'CV Screening', industry: 'HR & Legal' },
  { value: 'employee_evaluation', label: 'Employee Evaluation', industry: 'HR & Legal' },
  { value: 'succession_planning', label: 'Succession Planning', industry: 'HR & Legal' },
  { value: 'legal_research', label: 'Legal Research', industry: 'HR & Legal' },
  // Public & Government
  { value: 'benefits_eligibility_gov', label: 'Benefits Eligibility (Gov)', industry: 'Public & Gov' },
  { value: 'resource_allocation', label: 'Resource Allocation', industry: 'Public & Gov' },
  { value: 'policy_impact', label: 'Policy Impact Simulation', industry: 'Public & Gov' },
  // General
  { value: 'customer_support', label: 'Customer Support', industry: 'General' },
  { value: 'content_moderation', label: 'Content Moderation', industry: 'General' },
  { value: 'recommendation', label: 'Recommendation', industry: 'General' },
  { value: 'anomaly_detection', label: 'Anomaly Detection', industry: 'General' },
  { value: 'optimization', label: 'Optimization', industry: 'General' },
  { value: 'classification', label: 'Classification', industry: 'General' },
  { value: 'prediction', label: 'Prediction', industry: 'General' },
];

export function AIDecisionsTab({ currentUserEmail, initialData = null, initialLoading = false, light = false }) {
  const [decisions, setDecisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    company: '',
    decision_type: '',
    from_date: '',
    to_date: '',
  });
  const [userUuid, setUserUuid] = useState('');
  const [userEmail, setUserEmail] = useState(currentUserEmail || '');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const [showTestForm, setShowTestForm] = useState(false);
  const [testDecision, setTestDecision] = useState({
    ai_system: '',
    decision_type: '',
    decision: 'APPROVED',
    confidence_score: 80,
    human_reviewed: false,
  });

  const isSealed = localStorage.getItem(userKey('qais_blockchain_saved')) === 'true';

  // Colors based on light/dark mode
  const bgColor = light ? '#ffffff' : '#0f172a';
  const textColor = light ? '#1e293b' : '#e2e8f0';
  const headerBg = light ? '#f1f5f9' : '#1e293b';
  const headerText = light ? '#1e293b' : '#cbd5e1';
  const borderColor = light ? '#e2e8f0' : '#334155';
  const inputBg = light ? '#ffffff' : '#0f172a';
  const inputText = light ? '#1e293b' : '#e2e8f0';
  const disabledBg = light ? '#f1f5f9' : '#1e293b';
  const warningBg = light ? '#fef3c7' : '#1e293b';
  const warningColor = light ? '#d97706' : '#facc15';

  useEffect(() => {
    if (initialData) {
      setDecisions(initialData.decisions || []);
      setLoading(false);
      return;
    }
    if (initialLoading) {
      setLoading(true);
      return;
    }
    fetchDecisions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, initialData, initialLoading]);

  useEffect(() => {
    const loadProfileAndUser = async () => {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const user = sessionData?.session?.user;
        if (user) {
          setUserUuid(user.id);
          setUserEmail(user.email || currentUserEmail);
        }
        const onboard = localStorage.getItem(userKey('qais_onboarding'));
        if (onboard) {
          const data = JSON.parse(onboard);
          if (data.companyName) {
            setFilters(prev => ({ ...prev, company: data.companyName }));
          }
        }
      } catch (err) {
        console.warn('Error loading user or company:', err);
      }
    };
    loadProfileAndUser();
  }, [currentUserEmail]);

  const fetchDecisions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (!userUuid) {
        setDecisions([]);
        setLoading(false);
        return;
      }
      const params = new URLSearchParams();
      params.append('user_id', userUuid);
      if (filters.company) params.append('company', filters.company);
      if (filters.decision_type) params.append('decision_type', filters.decision_type);
      if (filters.from_date) params.append('from_date', filters.from_date);
      if (filters.to_date) params.append('to_date', filters.to_date);
      const url = `/api/ai-decisions?${params.toString()}`;
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      const response = await fetch(url, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setDecisions(data.decisions || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filters, userUuid]);

  useEffect(() => {
    if (userUuid) fetchDecisions();
  }, [fetchDecisions, userUuid]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const submitTestDecision = async () => {
    if (!isSealed) {
      alert('You must seal a compliance record first.');
      return;
    }
    if (!filters.company) {
      alert('Company name not loaded. Please complete your profile and refresh the page.');
      return;
    }
    if (!testDecision.ai_system.trim()) {
      alert('Please enter an AI System name.');
      return;
    }
    if (!testDecision.decision_type) {
      alert('Please select a Decision Type.');
      return;
    }
    if (!testDecision.decision) {
      alert('Please select a Decision value.');
      return;
    }

    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;
    if (!token) {
      alert('Not logged in.');
      return;
    }

    const payload = {
      company: filters.company,
      ai_system: testDecision.ai_system,
      decision_type: testDecision.decision_type,
      decision: testDecision.decision,
      confidence_score: testDecision.confidence_score,
      human_reviewed: testDecision.human_reviewed,
      user_id: sessionData.session.user.id,
      user_email: sessionData.session.user.email,
    };

    try {
      const res = await fetch('/api/log-ai-decision', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const responseText = await res.text();
      if (!res.ok) throw new Error(responseText);
      alert('AI decision logged successfully!');
      setShowTestForm(false);
      setTestDecision({
        ai_system: '',
        decision_type: '',
        decision: 'APPROVED',
        confidence_score: 80,
        human_reviewed: false,
      });
      await fetchDecisions();
    } catch (err) {
      alert('Failed: ' + err.message);
    }
  };

  const paginated = decisions.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const totalPages = Math.ceil(decisions.length / itemsPerPage);
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? '—' : d.toLocaleString();
  };

  const displayUserEmail = userEmail || '—';

  // ── Skeleton Loader ──
  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div style={{ fontSize: '30px', marginBottom: '12px' }}>⏳</div>
        <p style={{ color: '#64748b' }}>Loading AI decisions...</p>
      </div>
    );
  }

  return (
    <div>
      {!filters.company && (
        <div style={{ color: warningColor, marginBottom: '10px', padding: '8px', backgroundColor: warningBg, borderRadius: '8px' }}>
          ⚠️ Company name not loaded. Please complete your profile (Dashboard → Profile) and refresh.
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
        {isSealed ? (
          <button
            onClick={() => setShowTestForm(!showTestForm)}
            style={{ background: '#0c4a6e', border: '1px solid #0284c7', borderRadius: '6px', padding: '6px 12px', cursor: 'pointer', color: '#e2e8f0' }}
          >
            {showTestForm ? '− Close Form' : '+ Log AI Decision'}
          </button>
        ) : (
          <span style={{ color: warningColor, fontSize: '12px' }}>⚠️ Seal a compliance record first</span>
        )}
      </div>

      {showTestForm && isSealed && (
        <div style={{ backgroundColor: light ? '#f8fafc' : '#0a1628', padding: '16px', borderRadius: '8px', marginBottom: '20px', border: `1px solid ${borderColor}` }}>
          <h4 style={{ color: textColor, margin: '0 0 12px' }}>Log a test AI decision</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px,1fr))', gap: '12px', marginBottom: '12px' }}>
            <input type="text" placeholder="AI System *" value={testDecision.ai_system} onChange={e => setTestDecision({...testDecision, ai_system: e.target.value})} style={{ padding: '8px', borderRadius: '6px', border: `2px solid ${borderColor}`, background: inputBg, color: inputText }} />
            <select value={testDecision.decision_type} onChange={e => setTestDecision({...testDecision, decision_type: e.target.value})} style={{ padding: '8px', borderRadius: '6px', border: `2px solid ${borderColor}`, background: inputBg, color: inputText }}>
              <option value="">Select type *</option>
              {ALL_DECISION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <select value={testDecision.decision} onChange={e => setTestDecision({...testDecision, decision: e.target.value})} style={{ padding: '8px', borderRadius: '6px', border: `2px solid ${borderColor}`, background: inputBg, color: inputText }}>
              <option value="APPROVED">APPROVED</option>
              <option value="DENIED">DENIED</option>
              <option value="PASSED">PASSED</option>
              <option value="FLAGGED">FLAGGED</option>
            </select>
            <input type="number" placeholder="Confidence (0-100)" value={testDecision.confidence_score} onChange={e => setTestDecision({...testDecision, confidence_score: parseInt(e.target.value)})} style={{ padding: '8px', borderRadius: '6px', border: `2px solid ${borderColor}`, background: inputBg, color: inputText }} />
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: textColor }}>
              <input type="checkbox" checked={testDecision.human_reviewed} onChange={e => setTestDecision({...testDecision, human_reviewed: e.target.checked})} />
              Human reviewed
            </label>
          </div>
          <button
            onClick={submitTestDecision}
            disabled={!testDecision.ai_system.trim() || !testDecision.decision_type || !testDecision.decision}
            style={{ padding: '8px 16px', background: '#10b981', border: 'none', borderRadius: '6px', cursor: 'pointer', color: 'white', opacity: (!testDecision.ai_system.trim() || !testDecision.decision_type || !testDecision.decision) ? 0.5 : 1 }}
          >
            Submit
          </button>
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '20px' }}>
        <input type="text" placeholder="Company" value={filters.company} disabled style={{ padding: '6px 12px', borderRadius: '8px', fontSize: '13px', border: `2px solid ${borderColor}`, backgroundColor: disabledBg, cursor: 'not-allowed', color: inputText }} />
        <select value={filters.decision_type} onChange={(e) => handleFilterChange('decision_type', e.target.value)} style={{ padding: '6px 12px', borderRadius: '8px', fontSize: '13px', minWidth: '160px', border: `2px solid ${borderColor}`, background: inputBg, color: inputText }}>
          <option value="">All Types</option>
          {ALL_DECISION_TYPES.map(type => <option key={type.value} value={type.value}>{type.label} ({type.industry})</option>)}
        </select>

<input
          type="date"
          placeholder="From Date"
          value={filters.from_date}
          max={filters.to_date || undefined}
          onClick={(e) => e.target.showPicker && e.target.showPicker()}
          onChange={(e) => {
            const newFrom = e.target.value;
            if (filters.to_date && newFrom && newFrom > filters.to_date) {
              alert('"From Date" cannot be later than "To Date".');
              return;
            }
            handleFilterChange('from_date', newFrom);
          }}
          className="qais-date-input"
          style={{ padding: '6px 12px', borderRadius: '8px', fontSize: '13px', border: `2px solid ${borderColor}`, background: inputBg, color: inputText, cursor: 'pointer' }}
        />
        <input
          type="date"
          placeholder="To Date"
          value={filters.to_date}
          min={filters.from_date || undefined}
          onClick={(e) => e.target.showPicker && e.target.showPicker()}
          onChange={(e) => {
            const newTo = e.target.value;
            if (filters.from_date && newTo && newTo < filters.from_date) {
              alert('"To Date" cannot be earlier than "From Date".');
              return;
            }
            handleFilterChange('to_date', newTo);
          }}
          className="qais-date-input"
          style={{ padding: '6px 12px', borderRadius: '8px', fontSize: '13px', border: `2px solid ${borderColor}`, background: inputBg, color: inputText, cursor: 'pointer' }}
        />



        <button onClick={fetchDecisions} style={{ padding: '6px 12px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Refresh</button>
      </div>

      {error && <div style={{ color: 'red' }}>Error: {error}</div>}
      {!error && (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: bgColor }}>
              <thead style={{ backgroundColor: headerBg, borderBottom: `2px solid ${borderColor}` }}>
                <tr>
                  <th style={{ padding: '12px 8px', textAlign: 'left', color: headerText }}>Date</th>
                  <th style={{ padding: '12px 8px', textAlign: 'left', color: headerText }}>Company</th>
                  <th style={{ padding: '12px 8px', textAlign: 'left', color: headerText }}>User (Email)</th>
                  <th style={{ padding: '12px 8px', textAlign: 'left', color: headerText }}>AI System</th>
                  <th style={{ padding: '12px 8px', textAlign: 'left', color: headerText }}>Decision Type</th>
                  <th style={{ padding: '12px 8px', textAlign: 'left', color: headerText }}>Decision</th>
                  <th style={{ padding: '12px 8px', textAlign: 'left', color: headerText }}>Confidence</th>
                  <th style={{ padding: '12px 8px', textAlign: 'left', color: headerText }}>Human Reviewed</th>
                  <th style={{ padding: '12px 8px', textAlign: 'left', color: headerText, minWidth: '160px', whiteSpace: 'nowrap' }}>Attestation ID</th>
                  <th style={{ padding: '12px 8px', textAlign: 'left', color: headerText }}>Quantum‑Safe</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map(decision => {
                  const d = decision.data || decision;
                  return (
                    <tr key={d.certificate_id || decision.index || Math.random()} style={{ borderBottom: `1px solid ${borderColor}` }}>
                      <td style={{ padding: '10px 8px', color: textColor }}>{formatDate(d.audit_date || decision.audit_date)}</td>
                      <td style={{ padding: '10px 8px', color: textColor, fontWeight: '600' }}>{d.company || '—'}</td>
                      <td style={{ padding: '10px 8px', color: textColor }}>{displayUserEmail}</td>
                      <td style={{ padding: '10px 8px', color: textColor }}>{d.ai_system || '—'}</td>
                      <td style={{ padding: '10px 8px', color: textColor }}>{d.decision_type || '—'}</td>
                      <td style={{ padding: '10px 8px', color: textColor }}>
                        <span style={{ backgroundColor: d.decision === 'APPROVED' ? '#15803d' : '#991b1b', color: 'white', padding: '2px 8px', borderRadius: '20px', fontSize: '12px' }}>
                          {d.decision || '—'}
                        </span>
                      </td>
                      <td style={{ padding: '10px 8px', color: textColor }}>{d.confidence_score ?? '—'}</td>
                      <td style={{ padding: '10px 8px', color: textColor }}>{d.human_reviewed ? '✅ Yes' : '❌ No'}</td>
                      <td style={{ padding: '10px 8px', fontFamily: 'monospace', color: textColor, whiteSpace: 'nowrap' }}>{d.certificate_id || '—'}</td>
                      <td style={{ padding: '10px 8px', color: textColor }}>✅ Quantum‑Safe</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '20px' }}>
              <button onClick={() => setCurrentPage(p => Math.max(1, p-1))} disabled={currentPage === 1} style={{ background: headerBg, border: `1px solid ${borderColor}`, padding: '6px 12px', cursor: 'pointer', color: textColor }}>Previous</button>
              <span style={{ color: textColor }}>Page {currentPage} of {totalPages}</span>
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p+1))} disabled={currentPage === totalPages} style={{ background: headerBg, border: `1px solid ${borderColor}`, padding: '6px 12px', cursor: 'pointer', color: textColor }}>Next</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}